// Copyright (c) Docugami, Inc. All rights reserved.

import { ITextField, IStyle, TextField } from "@fluentui/react";
import { DgBlueColoredButtonProps, DgColoredButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily, dgTagFontFamily, userFontFamily } from "components/common/Fonts";
import { ExplorerTextButton } from "components/myContentExplorer/ExplorerStyles";
import { primaryTheme, emptyGray, appContentColor, themeBlue } from "components/shell/presentation/AppStyles";
import { Property } from "hooks/documentProperties";
import { useRef, useState, useEffect } from "react";
import styled from "styled-components";

const PropertyEditorRoot = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    flex: 1;
`

const PropertyViewContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`

const CommandBarContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    justify-content: flex-end;
    align-items: baseline;
    width: 100%;
`

const SaveButton = styled(DgColoredButton)`
    font-size: 12px;
    padding: 4px 7px;
`

const CancelButton = styled(ExplorerTextButton)`
    font-size: 12px;
    text-align: bottom;
    >span {
        justify-self: flex-end;
    }
`

const PreviewTitle = styled.span`
    font-family: ${dgFontFamily};
    font-size: 12px;
    font-weight: 500;
    color: ${appContentColor};
`;

export interface PropertyEditorProps {
    property?: Property;
    onEdit?: () => void;
    onSave: (property: Property) => void;
    validate: (property: Property) => boolean;
    onCancel: () => void;
    isSaving?: boolean;
    isNew?: boolean;
}

export const PropertyEditor = (props: PropertyEditorProps): JSX.Element => {
    const { property, onCancel } = props;
    const keyTextFieldRef = useRef<ITextField>();
    const valueTextFieldRef = useRef<ITextField>();

    const [editProperty, setEditProperty] = useState<Property | null>(null);

    const headerTextStyle: IStyle = {
        fontFamily: dgTagFontFamily,
        fontWeight: 700,
        fontSize: "16px",
        color: primaryTheme.palette.black,
        borderColor: themeBlue
    }

    const valueTextStyle: IStyle = {
        fontFamily: userFontFamily,
        fontWeight: 500,
        fontSize: "12px",
        color: primaryTheme.palette.black,
        borderColor: themeBlue
    }

    function onFieldClicked() {
        if (!!editProperty || !props.property) {
            return;
        }

        setEditProperty({ ...props.property });
        props.onEdit?.();
    }


    useEffect(() => {
        // If editing and the property changes to a different one OR we are actively saving the property, cancel the edit operation
        if (!!editProperty && (editProperty.spanId !== property?.spanId || !!props.isSaving)) {
            onCancel();
        }
        if (!!property) {
            setEditProperty({ ...property });
            if (!!valueTextFieldRef.current) {
                valueTextFieldRef.current.focus();
                // The input focus sets the cursor at the start of the text by default
                // When there is an existing value, we want the focused cursor to be at the end of the value
                valueTextFieldRef.current.setSelectionStart(valueTextFieldRef.current.value?.length ?? 0);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property]);

    const targetProperty = editProperty ?? props.property;
    return (
        <PropertyEditorRoot>
            <PropertyViewContainer>
                <div>
                    <PreviewTitle>{props.isNew ? 'Give your property a label:' : 'Label:'}</PreviewTitle>
                </div>
                <TextField
                    // @ts-ignore
                    componentRef={keyTextFieldRef}
                    autoFocus={!property?.label}
                    styles={{
                        field: {
                            ...headerTextStyle,
                            "::placeholder": {
                                ...headerTextStyle,
                                color: emptyGray,
                                fontStyle: "italic"
                            }
                        },
                        fieldGroup: {
                            "::after": {
                                borderColor: themeBlue
                            }
                        },
                        wrapper: {
                            "::after": {
                                borderColor: themeBlue
                            }
                        }
                    }}
                    value={targetProperty?.label ?? ''}
                    placeholder={"Enter a name..."}
                    onClick={onFieldClicked}
                    onKeyPress={ev => {
                        if (!!keyTextFieldRef.current?.value && ev.key === "Enter") {
                            valueTextFieldRef.current?.focus();
                        }
                    }}
                    onChange={(ev, value) => setEditProperty(x => {
                        if (!x) {
                            return x;
                        }

                        const newProp: Property = {
                            ...x,
                            label: value ?? ''
                        };

                        return newProp;
                    })} />
                <div>
                    <PreviewTitle>{props.isNew || !targetProperty?.value ? 'Add your content:' : 'Modify your content:'}</PreviewTitle>
                </div>
                <TextField
                    // @ts-ignore
                    componentRef={valueTextFieldRef}
                    autoFocus={!!property?.label}
                    styles={{
                        field: valueTextStyle,
                        fieldGroup: {
                            "::after": {
                                borderColor: themeBlue
                            }
                        }
                    }}
                    multiline
                    resizable={false}
                    rows={3}
                    value={targetProperty?.value ?? ''}
                    placeholder={!!targetProperty ? "Enter a value..." : undefined}
                    onClick={onFieldClicked}
                    onChange={(ev, value) => setEditProperty(x => {
                        if (!x) {
                            return x;
                        }

                        const newProp = {
                            ...x,
                            value: value ?? ''
                        };

                        return newProp;
                    })} />
                <CommandBarContainer>
                    <CancelButton onClick={onCancel}>Cancel</CancelButton>
                    <SaveButton controlButtonProps={DgBlueColoredButtonProps} onClick={() => props.onSave(editProperty!)} disabled={!editProperty || !props.validate(editProperty!)}>Save</SaveButton>
                </CommandBarContainer>
            </PropertyViewContainer>
        </PropertyEditorRoot>
    )
}
